<template lang="html">
  <section :id="id">
    <base-modal
      ref="baseModal"
      :maxHeight="modalHeight"
      :maxWidth="modalWidth"
      @on-base-modal-close="cancelModal"
    >
      <!-- header  -->
      <template v-slot:header>
        <div class="m-title">
          {{ $t("datepicker") }}
        </div>
      </template>

      <element-description
        v-if="isDescriptionReady"
        :label="descriptionText"
        :outer-container-class="'m-items'"
        :elem-description="desc"
        :value-container-class="'mt8 obj-select'"
        :focus="true"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ $t("datepickerData") }}
          </div>
        </div>
        <div class="expression">
          <search-box-simple
            id="datepicker-dp-search"
            class="search-box"
            :list="dataElementList"
            static-data-type="string"
            :value="datepickerStateExpression"
            :text-value="selectedTextValue"
            :has-new-token="true"
            :is-global="true"
            @on-search-box-item-selected="onDatepickerElementSelected"
            @on-search-box-blur="onDataElementInputBlur"
          />
        </div>
      </div>

      <div class="m-items">
        <div class="label-wrapper">
          <div class="label">
            {{ getMessageText(messages.FIELD_LIST_ITEM_LABEL) }}
          </div>
        </div>
        <div class="expression">
          <expressions
            id="labelExpression"
            :data-elements="allSources"
            :data-type="'string'"
            :initial-expression="labelExpression"
            :is-global="true"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <div class="m-items" v-if="isCreating">
        <div class="label-wrapper">
          <div class="label">
            {{ $t("default") }}
          </div>
        </div>
        <div class="expression">
          <expressions
            id="defaultDateExpression"
            :data-elements="allSources"
            :data-type="'date'"
            :initial-expression="defaultDateExpression"
            :is-global="true"
            :content-type="appContent.type"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>

      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" @click="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            @click="createBtn"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>
    </base-modal>
  </section>
</template>

<script lang="js">
  import { v4 as uuidv4 } from "uuid";
  import store from "@/store";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import Expressions from "@/components/Expressions"
  import ElementDescription from "@/components/ElementDescription";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import utils from "@/utils";
  import {DATA_ELEMENT_TYPE, MUTATION_NAME} from "@/static/global-variables";

  export default  {
    name: "add-datepicker-modal",
    components: {
      BaseModal,
      Expressions,
      ElementDescription,
      SearchBoxSimple
    },
    props: {
      id: {
        type: String
      },
      editData: {
        type: Object
      }
    },
    mounted () {
      if(this.editData) {
        this.labelExpression = this.editData.props.label;
        this.datepickerStateExpression = this.appContent.dataElements[this.editData.props.state];
        this.onDeDescriptionChanged(this.editData.props.description)
      }
      this.isDescriptionReady = true;
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      isSubmitDisabled() {
        if (this.isCreating) {
          return !this.labelExpression || utils.isExpressionWhiteSpace(this.datepickerStateExpression);
        } else {
          return !this.datepickerStateExpression || !this.labelExpression;
        }
      },
      appId() {
        return dragAndDrop.getAppId();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      allSources() {
        let output = null;
        if (this.appContent) {
          output = this.appContent.dataElements || {};
        } else {
          console.error(`No app content found for appId ${this.appId}`);
        }
        return output;
      },
      dataElementList() {
        return expressionBuilder.findDataElements(this.allSources, DATA_ELEMENT_TYPE.DATEPICKER);
      },
      desc() {
        return this.editData ? this.editData.props.description : "";
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    data () {
      return {
        modalHeight: 500,
        modalWidth: 700,
        labelExpression: null,
        datepickerStateExpression: null,
        elementDescription: null,
        selectedTextValue: null,
        isCreating: null,
        defaultDateExpression: null,
        isDescriptionReady: false
      }
    },
    methods: {
      cancelModal() {
        const EVENT = "on-add-modal-close";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, {id: this.id});
        }
      },
      createBtn() {
        let dataElementId;
        if (this.isCreating) {
          dataElementId = uuidv4();
          const dataElement = {
            appId: this.appId,
            id: dataElementId,
            displayElementRefCount: 0,
            name: this.datepickerStateExpression.value,
            description: "",
            fields: { Selection: { dataType: "Date" } },
            type: DATA_ELEMENT_TYPE.DATEPICKER,
            default: this.defaultDateExpression,

          }
          store.commit(MUTATION_NAME.EDIT_DATA_PALETTES, dataElement);
        } else {
          dataElementId = this.datepickerStateExpression.id;
        }

        const obj = {
          modal: {
            id: this.id,
            create: "Datepicker",
            editElementId: this.editData ? this.editData.id: null
          },
          props: {
            description: this.elementDescription,
            data: {
              type: "Date Picker",
              label: this.labelExpression,
              state: dataElementId
            }
          }
        };

        const info = {
          appId: this.appId,
          dataElementId,
          prevState: this.editData ? this.editData.props.state : ""
        }
        store.commit(MUTATION_NAME.EDIT_DROPDOWN_REFERENCE, info);

        const EVENT = "on-add-modal-done";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, obj);
        }
      },

      onConfigExpressionValid(ctx) {
        const expValue = ctx.status ? ctx : null;
        const confExp = expValue ? expValue.confExpression : null;

        if(ctx.id === "defaultDateExpression") {
          this.defaultDateExpression = confExp;
        } else {
          this.labelExpression = confExp;
        }
      },
      onDeDescriptionChanged(input) {
        this.elementDescription = input;
      },
      onDatepickerElementSelected(ctx) {
        if ( !ctx || ctx.isEmpty) {
          this.datepickerStateExpression = null;
          this.isCreating = false;
        } else {
          const isStaticType = ctx.type === expressionBuilder.EXPRESSION_TYPE.STATIC;
          const isStringDataType = ctx.dataType === expressionBuilder.EXPRESSION_TYPE.STRING;
          this.isCreating = isStaticType && isStringDataType;
          this.datepickerStateExpression = this.isCreating ? ctx : this.appContent.dataElements[ctx.id];
        }
      },
      onDataElementInputBlur() {
        if(!this.datepickerStateExpression) {
          // clear the search box
          this.selectedTextValue = "";
          this.$nextTick(() => {
            // must trigger update to clear the box, set to null
            this.selectedTextValue = null;
          });
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .m-items {
    grid-template-columns: 8rem auto;
    margin-bottom: 10px;
    color: var(--DEFAULT_TEXT_BLACK);
    .label {
      overflow-wrap: anywhere;
      padding-top: 0.3rem;
    }
    .label-wrapper {
      padding-right: 1rem;
    }
    .expression,
    .label {
      display: flex;
      align-items: center;
      min-height: 2.5rem;
    }
    .progress-overflow {
      padding-top: 0;
    }
  }
  .m-title {
    text-align: left;
  }
</style>
